/**
 * Settings tab of the ONE Profile drawer.
 *
 * Hosts the language preference above the mass-notification destinations,
 * mirroring the AIM/SMS Settings-tab ordering (notification preferences
 * first, destinations below).
 */
import { Box, Text, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { SelectInput } from '@themes/aegix';
import { useAuthActions, type AllauthUser } from '@aegix/auth-identity';
import { ProfileContactAdapter } from '@/components/ProfileContactAdapter';
import { OneNotificationPreferences } from '@/components/profile/OneNotificationPreferences';
import { toaster } from '@/components/ui/toaster';
import { LANGUAGE_OPTIONS } from '@/i18n/config';

export function ProfileSettingsTab() {
  const { t, i18n } = useTranslation();
  const { updateLanguage } = useAuthActions<AllauthUser>();

  return (
    <VStack gap={5} align="stretch">
      {/* Language selector */}
      <Box>
        <Text fontSize="sm" fontWeight="600" color="text.primary" mb={2}>
          {t('profile.language')}
        </Text>
        <SelectInput
          label={t('profile.selectLanguage')}
          name="language"
          option={LANGUAGE_OPTIONS}
          value={i18n.language ? i18n.language.split('-')[0] : 'en'}
          onChange={async (val) => {
            // Must settle before the PATCH: the language reconciliation
            // effect reads the same `i18nextLng` key this writes, so if
            // `set_user` lands first the effect still sees the old value
            // and PATCHes the previous language back.
            await i18n.changeLanguage(val);
            try {
              await updateLanguage(val);
            } catch (error) {
              console.error(
                'Failed to sync language preference to backend:',
                error,
              );
              toaster.create({
                description: t('profile.languageSyncError', {
                  defaultValue:
                    'Failed to synchronize language preference with backend.',
                }),
                type: 'error',
              });
            }
          }}
          e2eTitle="profile-language-select"
        />
      </Box>

      {/* ONE push preference toggles — the two
          ONE-specific categories, PATCHed to /api/v2/user/{id}/. */}
      <OneNotificationPreferences />

      {/* Mass-notification destinations (ADR-141 §5).
          Renders nothing unless the org has the capability — the
          adapter gates on the me/contact endpoint's 403. */}
      <ProfileContactAdapter />
    </VStack>
  );
}
